import { View, Text, styled, useTheme } from 'tamagui'
import { useEffect, useRef, useMemo } from 'react'

const MenuContainer = styled(View, {
  position: 'fixed' as any,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingVertical: 4,
  minWidth: 180,
  zIndex: 3000,
})

const MenuItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 32,
  paddingHorizontal: 12,
  gap: 8,
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
  variants: {
    danger: {
      true: {
        hoverStyle: {
          backgroundColor: 'rgba(207, 62, 79, 0.15)',
        },
      },
    },
  } as const,
})

const MenuLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$surfaceForeground',
  flex: 1,
  variants: {
    danger: {
      true: {
        color: '$error',
      },
    },
  } as const,
})

const MenuSeparator = styled(View, {
  height: 1,
  backgroundColor: '$borderDefault',
  marginVertical: 4,
  marginHorizontal: 8,
})

const SubMenuLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  paddingHorizontal: 12,
  paddingVertical: 4,
})

export interface ContextMenuItem {
  id: string
  label: string
  icon?: React.ReactNode
  danger?: boolean
  action: () => void
}

export interface ContextMenuSection {
  label?: string
  items: ContextMenuItem[]
}

interface ContextMenuProps {
  x: number
  y: number
  sections: ContextMenuSection[]
  onClose: () => void
}

export function ContextMenu({ x, y, sections, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null)
  const theme = useTheme()

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  // Clamp position to viewport
  const adjustedX = Math.min(x, window.innerWidth - 200)
  const adjustedY = Math.min(y, window.innerHeight - sections.reduce((a, s) => a + s.items.length * 32 + 8, 0))

  return (
    <MenuContainer
      ref={ref as any}
      // @ts-expect-error -- web position
      style={{ position: 'fixed', left: adjustedX, top: adjustedY, boxShadow: theme.shadowCard?.val || '0 4px 12px rgba(0,0,0,0.4)' }}
    >
      {sections.map((section, sIdx) => (
        <View key={sIdx}>
          {sIdx > 0 && <MenuSeparator />}
          {section.label && <SubMenuLabel>{section.label}</SubMenuLabel>}
          {section.items.map((item) => (
            <MenuItem
              key={item.id}
              danger={item.danger}
              onPress={() => {
                item.action()
                onClose()
              }}
            >
              {item.icon && <View width={16} alignItems="center">{item.icon}</View>}
              <MenuLabel danger={item.danger}>{item.label}</MenuLabel>
            </MenuItem>
          ))}
        </View>
      ))}
    </MenuContainer>
  )
}

export { MenuSeparator }
